/**
 * The capability ids: one closed list of what RecordOS can make, can make
 * only after Build, or cannot make yet. The manifest (`manifest.ts`) gives
 * each id its words, options and limits; `CapabilityId` is the union a
 * `Record` of words is checked against, so a new id is a compile error until
 * it has words.
 *
 * Two kinds of family.
 *
 *   generated   one id per member of a list the code already enforces (the
 *               value types, the card kinds, the posting patterns ...). A member
 *               added to the code's list is a new id with no edit here.
 *   written     the lists below, where no code list exists: the parts of a
 *               record, a form, a view, a card, a link, the team, memory,
 *               what comes in, the workspace, and the asks no part holds.
 */
import type { SetupDraftField } from '../compose-author';
import { FILE_KINDS, type FileKind } from '../agent-files';
import { FORMULA_NODE_KINDS, type FormulaNode } from '../formula';
import { VALUE_TYPE_IDS, type ValueTypeId } from '../generated/value-types';
import { INSIGHT_CARD_IDS, type InsightCardId } from '../insights';
import { PERMISSION_GROUPS, type PermissionGroup } from '../roles';
import { POSTING_PATTERNS, type PostingPattern } from '../ledger/patterns';

/* ------------------------------------------------------------ the levels */

/**
 * The five levels a part is chosen at (docs/records-platform-architecture.md,
 * "five levels": data, surface, arrangement, lifecycle, view), and the groups
 * that hold across a workspace (capabilities.md §1).
 */
export const CAPABILITY_LEVELS = [
  'workspace',
  'data',
  'surface',
  'arrangement',
  'lifecycle',
  'view',
  'insight',
  'automation',
  'people',
  'memory',
  'input',
] as const;
export type CapabilityLevel = (typeof CAPABILITY_LEVELS)[number];

export const isCapabilityLevel = (value: unknown): value is CapabilityLevel =>
  typeof value === 'string' && (CAPABILITY_LEVELS as readonly string[]).includes(value);

/**
 *   shipped           a setup or OS makes it today, and a screen draws it
 *   stored_not_drawn  written and kept, read by no screen
 *   no_engine         accepted and stored, and nothing runs it
 *   planned           a later level of the architecture; nothing holds it yet
 *   not_in_recordos   an ask no part holds; only its closest fit can be made
 */
export const CAPABILITY_STATUSES = ['shipped', 'stored_not_drawn', 'no_engine', 'planned', 'not_in_recordos'] as const;
export type CapabilityStatus = (typeof CAPABILITY_STATUSES)[number];

/* --------------------------------------------------- the written lists */

/** What a field may carry besides its type, by the draft key that carries it. */
export const FIELD_OPTION_KEYS = [
  'required',
  'sticky',
  'help',
  'shape',
  'option_sign_tone',
  'filter',
  'max_chosen',
  'starts_with',
  'show_when',
  'editable_when',
  'once',
  'lines_start',
  'open_row',
] as const;
export type FieldOptionKey = (typeof FIELD_OPTION_KEYS)[number];

/** The `SetupDraftField` key each field option is written in. */
export const FIELD_OPTION_DRAFT_KEY = {
  required: 'required',
  sticky: 'sticky',
  help: 'help',
  shape: 'shape',
  option_sign_tone: 'options',
  filter: 'filter',
  max_chosen: 'maxChosen',
  starts_with: 'startsWith',
  show_when: 'showWhen',
  editable_when: 'editableWhen',
  once: 'startsOnce',
  lines_start: 'lines',
  open_row: 'openRow',
} as const satisfies Record<FieldOptionKey, keyof SetupDraftField>;

export const RECORD_KEYS = ['record', 'list', 'list_money', 'direction', 'icon', 'entry_noun', 'seed_rows', 'bindings', 'archive', 'rename', 'must_be', 'kit'] as const;
export const SURFACE_KEYS = ['form', 'sheet', 'catalogue', 'counter', 'timer'] as const;
export const ARRANGEMENT_KEYS = ['sections', 'steps', 'tabs'] as const;
export const LIFECYCLE_KEYS = ['statuses', 'approval', 'reminders', 'tasks'] as const;
export const VIEW_KEYS = ['list', 'summary', 'stored_options', 'view_for_now', 'board', 'calendar', 'resource_grid'] as const;
/**
 * What is printed from an entry: a template (Typst, `routes/doc-templates.ts`),
 * a numbered series (`routes/doc-series.ts`), an issued document
 * (`routes/issued-documents.ts`) and the payment QR a template carries. Made
 * after Build, never inside a setup draft.
 */
export const DOCUMENT_KEYS = ['template', 'series', 'issue', 'payment_qr'] as const;
export const INSIGHT_KEYS = ['agg', 'scope', 'where', 'where_list', 'date_condition', 'group_by', 'via', 'row_recipe', 'cross_record', 'dashboard', 'rows_count'] as const;
export const PEOPLE_KEYS = ['owner', 'role', 'access', 'invite', 'reports_to', 'handover', 'ask_right'] as const;
export const MEMORY_KEYS = ['fact', 'setup_thread', 'website', 'unmet_need'] as const;
export const INPUT_KEYS = ['import', 'entries_from_file', 'web_search', 'place', 'app_research'] as const;
export const WORKSPACE_KEYS = ['name', 'currency', 'logo', 'timezone', 'language', 'financial_year', 'tax', 'how_money_works'] as const;
/**
 * How money is kept (docs/plans/ledger.md, step 12.6): the parts beside the
 * posting patterns (`money.pattern.*`) that a person meets on a screen or
 * through OS, each with its own entry so a refusal names what it is about.
 */
export const MONEY_KEYS = [
  'rule',
  'accounts',
  'category',
  'to_sort',
  'match',
  'statement',
  'charge_run',
  'party_period',
  'close',
  'correct',
  'see_money',
  'accountant_view',
] as const;

/**
 * Asks no part holds (the 9.9 board's notes and concept.md "Not possible
 * yet"), each with the level it would live at. The manifest gives each its
 * closest fit.
 */
export const NOT_YET_LEVEL = {
  send_messages: 'automation',
  gst_filing: 'insight',
  payroll_payment: 'automation',
  bank_feed: 'input',
  barcode_scanning: 'input',
  stock_from_lines: 'automation',
  settle_against_bills: 'automation',
  third_lines_level: 'data',
} as const satisfies Readonly<Record<string, CapabilityLevel>>;
export const NOT_YET_KEYS = Object.keys(NOT_YET_LEVEL) as readonly (keyof typeof NOT_YET_LEVEL)[];
export type NotYetKey = keyof typeof NOT_YET_LEVEL;

/* ------------------------------------------------------------- the ids */

/** A card kind without its `insight.` prefix: `stat`, `delta` ... */
type InsightCardName = InsightCardId extends `insight.${infer Name}` ? Name : never;
const INSIGHT_CARD_NAMES = INSIGHT_CARD_IDS.map((id) => id.replace(/^insight\./, '')) as readonly InsightCardName[];

export type CapabilityId =
  | `field.type.${ValueTypeId}`
  | `field.option.${FieldOptionKey}`
  | `record.${(typeof RECORD_KEYS)[number]}`
  | `formula.piece.${FormulaNode['kind']}`
  | `surface.${(typeof SURFACE_KEYS)[number]}`
  | `arrangement.${(typeof ARRANGEMENT_KEYS)[number]}`
  | `lifecycle.${(typeof LIFECYCLE_KEYS)[number]}`
  | `view.${(typeof VIEW_KEYS)[number]}`
  | `document.${(typeof DOCUMENT_KEYS)[number]}`
  | `insight.card.${InsightCardName}`
  | `insight.${(typeof INSIGHT_KEYS)[number]}`
  | `money.pattern.${PostingPattern}`
  | `money.${(typeof MONEY_KEYS)[number]}`
  | `people.group.${PermissionGroup}`
  | `people.${(typeof PEOPLE_KEYS)[number]}`
  | `memory.${(typeof MEMORY_KEYS)[number]}`
  | `input.file.${FileKind}`
  | `input.${(typeof INPUT_KEYS)[number]}`
  | `workspace.${(typeof WORKSPACE_KEYS)[number]}`
  | `not_yet.${NotYetKey}`;

/**
 * Every family's members, by its prefix, in the order ids are listed: by
 * level, then the families of a level in this order, then list order.
 */
export const CAPABILITY_ID_SOURCES = {
  generated: {
    'field.type': VALUE_TYPE_IDS,
    'formula.piece': FORMULA_NODE_KINDS,
    'insight.card': INSIGHT_CARD_NAMES,
    'money.pattern': POSTING_PATTERNS,
    'people.group': PERMISSION_GROUPS,
    'input.file': FILE_KINDS,
  },
  written: {
    workspace: WORKSPACE_KEYS,
    'field.option': FIELD_OPTION_KEYS,
    record: RECORD_KEYS,
    surface: SURFACE_KEYS,
    arrangement: ARRANGEMENT_KEYS,
    lifecycle: LIFECYCLE_KEYS,
    view: VIEW_KEYS,
    document: DOCUMENT_KEYS,
    insight: INSIGHT_KEYS,
    people: PEOPLE_KEYS,
    memory: MEMORY_KEYS,
    input: INPUT_KEYS,
    money: MONEY_KEYS,
  },
  notYet: NOT_YET_LEVEL,
} as const;

type FamilyPrefix = keyof (typeof CAPABILITY_ID_SOURCES)['generated'] | keyof (typeof CAPABILITY_ID_SOURCES)['written'];

/** The level of every family, by its first segment. */
const LEVEL_OF_ROOT: Readonly<Record<string, CapabilityLevel>> = {
  workspace: 'workspace',
  field: 'data',
  record: 'data',
  formula: 'data',
  surface: 'surface',
  arrangement: 'arrangement',
  lifecycle: 'lifecycle',
  view: 'view',
  document: 'view',
  insight: 'insight',
  money: 'automation',
  people: 'people',
  memory: 'memory',
  input: 'input',
};

/** The families within one level, in the order they are listed. */
const FAMILY_ORDER: readonly FamilyPrefix[] = [
  'workspace',
  'field.type',
  'field.option',
  'record',
  'formula.piece',
  'surface',
  'arrangement',
  'lifecycle',
  'view',
  'document',
  'insight.card',
  'insight',
  'money.pattern',
  'money',
  'people.group',
  'people',
  'memory',
  'input.file',
  'input',
];

export type CapabilityIdSources = {
  readonly generated: Readonly<Record<keyof (typeof CAPABILITY_ID_SOURCES)['generated'], readonly string[]>>;
  readonly written: Readonly<Record<keyof (typeof CAPABILITY_ID_SOURCES)['written'], readonly string[]>>;
  readonly notYet: Readonly<Record<string, CapabilityLevel>>;
};

/** Every id the sources make, by level, then family, then list order. */
export const buildCapabilityIds = (sources: CapabilityIdSources): readonly string[] => {
  const members = (prefix: FamilyPrefix): readonly string[] =>
    prefix in sources.generated
      ? sources.generated[prefix as keyof CapabilityIdSources['generated']]
      : sources.written[prefix as keyof CapabilityIdSources['written']];
  const out: string[] = [];
  for (const level of CAPABILITY_LEVELS) {
    for (const prefix of FAMILY_ORDER) {
      if (LEVEL_OF_ROOT[prefix.split('.')[0]!] !== level) continue;
      for (const member of members(prefix)) out.push(`${prefix}.${member}`);
    }
    for (const [key, at] of Object.entries(sources.notYet)) if (at === level) out.push(`not_yet.${key}`);
  }
  return out;
};

/** Every capability id, in a fixed order: by level, then family, then list order. */
export const CAPABILITY_IDS = buildCapabilityIds(CAPABILITY_ID_SOURCES) as readonly CapabilityId[];

/** The level an id is chosen at, read from its prefix. */
export const levelOf = (id: CapabilityId): CapabilityLevel => {
  const [root, key] = id.split('.') as [string, string];
  if (root === 'not_yet') return NOT_YET_LEVEL[key as NotYetKey];
  return LEVEL_OF_ROOT[root]!;
};
